import { useState, useEffect, useCallback } from "react";
import { apiService } from "../services/api";
import { taskService, TaskSummary, TASKS_CHANGED_EVENT } from "../services/taskService";

export interface NotificationCounts {
    pendingOrderRequests: number;
    unreadContactMessages: number;
    /** The signed-in person's tasks: open, overdue, due today, unread alerts. */
    taskSummary: TaskSummary | null;
    /** Re-read the task badges now (e.g. after reading alerts). */
    refreshTaskSummary: () => void;
}

// Shared by Header (bell) and Sidebar (nav item badges) via Layout, which
// calls this once and passes the result down — calling it independently
// in both would double-poll the same two endpoints every interval.
export const useNotificationCounts = (
    refreshInterval: number = 60000,
    enabled: boolean = true,
): NotificationCounts => {
    const [pendingOrderRequests, setPendingOrderRequests] = useState(0);
    const [unreadContactMessages, setUnreadContactMessages] = useState(0);

    useEffect(() => {
        if (!enabled) return; // guest order requests are outside Investor's scope
        let cancelled = false;
        const fetchPending = async () => {
            try {
                const result = await apiService.get<{ total: number }>(
                    "/guest-orders?status=PENDING&limit=1",
                );
                if (!cancelled) setPendingOrderRequests(result.total ?? 0);
            } catch {
                // non-critical badge
            }
        };
        fetchPending();
        const interval = setInterval(fetchPending, refreshInterval);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [enabled, refreshInterval]);

    useEffect(() => {
        if (!enabled) return; // contact messages are outside Investor's scope
        let cancelled = false;
        const fetchUnread = async () => {
            try {
                const result = await apiService.get<{ unread_count: number }>(
                    "/contact-messages?status=NEW&limit=1",
                );
                if (!cancelled) setUnreadContactMessages(result.unread_count ?? 0);
            } catch {
                // non-critical badge
            }
        };
        fetchUnread();
        const interval = setInterval(fetchUnread, refreshInterval);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [enabled, refreshInterval]);

    const [taskSummary, setTaskSummary] = useState<TaskSummary | null>(null);
    const fetchTaskSummary = useCallback(async () => {
        try {
            setTaskSummary(await taskService.summary());
        } catch {
            // non-critical badge
        }
    }, []);

    useEffect(() => {
        if (!enabled) return; // tasks are outside Investor's scope
        fetchTaskSummary();
        const interval = setInterval(() => {
            if (document.visibilityState === "visible") fetchTaskSummary();
        }, refreshInterval);
        // New assignments show up as soon as someone comes back to the tab.
        const onFocus = () => fetchTaskSummary();
        window.addEventListener("focus", onFocus);
        // Changes made on this tab (Tasks page, drawers) — settle, then re-read.
        let settle: ReturnType<typeof setTimeout> | undefined;
        const onChanged = () => {
            clearTimeout(settle);
            settle = setTimeout(fetchTaskSummary, 1000);
        };
        window.addEventListener(TASKS_CHANGED_EVENT, onChanged);
        return () => {
            clearInterval(interval);
            clearTimeout(settle);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener(TASKS_CHANGED_EVENT, onChanged);
        };
    }, [enabled, refreshInterval, fetchTaskSummary]);

    return { pendingOrderRequests, unreadContactMessages, taskSummary, refreshTaskSummary: fetchTaskSummary };
};
